import { useEffect, useMemo, useRef, useState } from "react";
import { Popover as PopoverPrimitive } from "radix-ui";

export interface TourStepProps {
  /** CSS selector, typically `[data-tour-target="key"]`. Omit for a centered,
   *  un-anchored step (no spotlight) — used for conceptual/wrap-up steps. */
  targetSelector?: string;
  /** 1-indexed. */
  stepNumber: number;
  totalSteps: number;
  title: string;
  body: string;
  primaryLabel: string;
  onPrimary: () => void;
  onSkip: () => void;
  /** Defaults to "bottom". */
  side?: "top" | "right" | "bottom" | "left";
}

const POLL_INTERVAL_MS = 200;
const POLL_TIMEOUT_MS = 2000;

export function TourStep({
  targetSelector,
  stepNumber,
  totalSteps,
  title,
  body,
  primaryLabel,
  onPrimary,
  onSkip,
  side = "bottom",
}: TourStepProps) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const spotlightElRef = useRef<HTMLElement | null>(null);

  // Find target (poll up to POLL_TIMEOUT_MS). No selector → centered step, skip.
  useEffect(() => {
    if (!targetSelector) return;
    const start = Date.now();
    let cancelled = false;

    function tick() {
      if (!targetSelector) return;
      if (cancelled) return;
      const el = document.querySelector(targetSelector) as HTMLElement | null;
      if (el) {
        setTarget(el);
        return;
      }
      if (Date.now() - start >= POLL_TIMEOUT_MS) {
        setGaveUp(true);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    }
    tick();

    return () => {
      cancelled = true;
    };
  }, [targetSelector]);

  // Apply spotlight attribute when target resolves; clean up on unmount or target change.
  useEffect(() => {
    if (!target) return;
    target.setAttribute("data-tour-spotlight", "");
    spotlightElRef.current = target;
    return () => {
      spotlightElRef.current?.removeAttribute("data-tour-spotlight");
      spotlightElRef.current = null;
    };
  }, [target]);

  // Click-through: clicking the spotlighted target advances the step.
  useEffect(() => {
    if (!target) return;
    const handler = () => onPrimary();
    target.addEventListener("click", handler);
    return () => target.removeEventListener("click", handler);
  }, [target, onPrimary]);

  // Virtual anchor ref for Radix Popover. We only render the Anchor when `target`
  // is non-null, which satisfies React 19's non-nullable RefObject<T>. Memoize by
  // target identity so Radix doesn't tear down and remeasure on unrelated renders.
  const virtualRef = useMemo(
    () => (target ? { current: target } : null),
    [target],
  );

  // No selector → render the centered card immediately (no poll, no spotlight).
  const showFallback = !targetSelector || (gaveUp && !target);

  // Shared tooltip body — rendered inside either Radix popover content or a plain
  // fixed-centered div (fallback). Radix writes inline position styles that win
  // over Tailwind utilities, so we can't just add fixed-center classes to Content;
  // we have to escape the portal entirely when there's no valid anchor.
  const body_content = (
    <>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {stepNumber} / {totalSteps}
      </div>
      <div className="text-sm font-semibold mb-1">{title}</div>
      <div className="text-sm text-muted-foreground mb-4">{body}</div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip tour
        </button>
        <button
          type="button"
          onClick={onPrimary}
          className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 transition-opacity"
        >
          {primaryLabel}
        </button>
      </div>
    </>
  );

  if (showFallback) {
    return (
      <div
        role="dialog"
        className="fixed left-1/2 top-1/2 z-[60] w-[min(360px,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-md border bg-popover p-4 text-popover-foreground shadow-md"
      >
        {body_content}
      </div>
    );
  }

  return (
    <PopoverPrimitive.Root open>
      {virtualRef ? (
        <PopoverPrimitive.Anchor virtualRef={virtualRef} />
      ) : null}
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          sideOffset={12}
          align="center"
          avoidCollisions
          collisionPadding={16}
          className="z-[60] w-[min(360px,90vw)] rounded-md border bg-popover p-4 text-popover-foreground shadow-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {body_content}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
